<script lang="ts" generics="T extends string">
  /**
   * Two or three options side by side, each a short name over a note:
   * "Solo / Together", "Player / Spectator". Four make two rows of two (a
   * profile's "Not shown / Female / Male / Other", TwinTen ADR 0073). A `radiogroup`, the sibling of
   * `RadioCards` for choices that fit a tile rather than a card. TwinTen's
   * picker "who" tiles.
   *
   * Chosen is a 2px accent border and a 10% wash of the accent, the same as a
   * chosen `ChoiceCard`. One Tab stop, the arrow keys move and select
   * (`radioKeys.ts`). The parent owns `value`.
   *
   * Since 1.7.0:
   *
   * - an option's `lang` is set on its name (a language picker's
   *   "Nederlands", said in that voice);
   * - **`deselectable`** with **`onclear`**: a second press on the chosen
   *   tile clears the choice (a profile's sex, taken back off). A radio
   *   cannot be unchosen, so a deselectable group is not a radio group: it is
   *   a `group` of toggle buttons (`aria-pressed`), at most one pressed, each
   *   its own Tab stop, which is what a screen reader then announces ("Male,
   *   toggle button, pressed"; pressed again, "not pressed"). The arrow keys
   *   are not the group's there. It looks the same. `value` is `null` (or
   *   any value no option has) while nothing is chosen; `onclear` is called
   *   on the second press, and the game sets that.
   *
   * Test ids: `testid` on the group, each option's `testid` on its tile.
   */
  import { handleRadioKeydown, rovingTabIndex } from '../radioKeys.js';
  import type { ChoiceTileOption } from '../types.js';

  interface Props {
    /** The group's accessible name: what is being chosen. */
    readonly label: string;
    /** The chosen option's value; `null` (since 1.7.0) while nothing is. */
    readonly value: T | null;
    readonly options: readonly ChoiceTileOption<T>[];
    readonly onchange: (value: T) => void;
    readonly testid?: string | undefined;
    // ── 1.7.0 ──
    /** A second press on the chosen tile clears it (`onclear`): toggle buttons, not radios. */
    readonly deselectable?: boolean;
    /** The chosen tile was pressed again, in a `deselectable` group. */
    readonly onclear?: (() => void) | undefined;
  }
  const {
    label,
    value,
    options,
    onchange,
    testid,
    deselectable = false,
    onclear,
  }: Props = $props();

  const uid = $props.id();
  let group = $state<HTMLDivElement | undefined>();
  const checkedIndex = $derived(options.findIndex((option) => option.value === value));

  function onKeydown(event: KeyboardEvent): void {
    handleRadioKeydown(
      event,
      group,
      checkedIndex,
      options.length,
      (index) => {
        const next = options[index];
        if (next !== undefined && next.value !== value && next.disabled !== true) {
          onchange(next.value);
        }
      },
      (index) => options[index]?.disabled === true,
    );
  }

  const isOff = (index: number): boolean => options[index]?.disabled === true;

  function press(option: ChoiceTileOption<T>): void {
    if (option.value !== value) onchange(option.value);
    else if (deselectable) onclear?.();
  }
</script>

<div
  class="ui-choice-tiles"
  class:ui-choice-tiles--three={options.length === 3 || options.length > 4}
  role={deselectable ? 'group' : 'radiogroup'}
  aria-label={label}
  data-testid={testid}
  bind:this={group}
>
  {#each options as option, index (option.value)}
    <button
      type="button"
      role={deselectable ? undefined : 'radio'}
      aria-checked={deselectable ? undefined : value === option.value}
      aria-pressed={deselectable ? value === option.value : undefined}
      aria-labelledby={`${uid}-${String(index)}-name`}
      aria-describedby={option.note !== undefined ? `${uid}-${String(index)}-note` : undefined}
      tabindex={deselectable
        ? undefined
        : rovingTabIndex(index, checkedIndex, isOff, options.length)}
      class="ui-choice-tile"
      class:ui-choice-tile--on={value === option.value}
      disabled={option.disabled === true}
      data-testid={option.testid}
      onclick={() => press(option)}
      onkeydown={deselectable ? undefined : onKeydown}
    >
      <span class="ui-choice-tile-name" id={`${uid}-${String(index)}-name`} lang={option.lang}
        >{option.name}</span
      >
      {#if option.note !== undefined}
        <span class="ui-choice-tile-note" id={`${uid}-${String(index)}-note`}>{option.note}</span>
      {/if}
    </button>
  {/each}
</div>

<style>
  .ui-choice-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--ui-space-2);
  }

  .ui-choice-tiles--three {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .ui-choice-tile {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    min-height: 2.75rem;
    padding: 0.75rem 0.5rem;
    border: 2px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    background: var(--ui-card);
    color: var(--ui-ink);
    font: var(--ui-text-body);
    text-align: center;
  }

  .ui-choice-tile--on {
    border-color: var(--ui-accent);
    background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-card));
  }

  .ui-choice-tile:disabled {
    opacity: 0.5;
  }

  .ui-choice-tile:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 2px;
  }

  .ui-choice-tile-name {
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-choice-tile-note {
    font: var(--ui-text-eyebrow);
    font-weight: 400;
    color: var(--ui-ink-2);
  }
</style>
